import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

/**
 * FWAQR — Branded FWA Expo QR screen.
 *
 * Shannon opens /fwa-qr on her phone at the booth. Attendee opens their
 * phone camera, points it at Shannon's screen, taps the notification —
 * lands on /fwa where the identity gate captures who they are and
 * routes them into the correct experience (owner triage, vendor apply,
 * ADA walkthrough, press packet, etc.).
 *
 * QR is generated CLIENT-SIDE via qrcode.react — no third-party service.
 * URL: /fwa-qr (aliased at /qr-fwa also).
 */

const BG = "#020A1F";
const CYAN = "#00FFFF";
const WHITE = "#FFFFFF";
const SILVER = "#A0A0A0";

const MONO = "'JetBrains Mono', 'SF Mono', Menlo, monospace";
const SANS = "'Inter', -apple-system, sans-serif";

const injectFonts = () => {
  const ID = "fwaqr-fonts";
  if (document.getElementById(ID)) return;
  const l = document.createElement("link");
  l.id = ID; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@600;700&family=Inter:wght@300;400;600;700;800&display=swap";
  document.head.appendChild(l);
};

const FWAQR = () => {
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    injectFonts();
    if (typeof window !== "undefined") setOrigin(window.location.origin);
  }, []);

  const fwaUrl = useMemo(() => (origin ? `${origin}/fwa` : ""), [origin]);

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(fwaUrl); } catch (e) { void e; }
  };

  const shareCard = async () => {
    if (!navigator.share) { copyUrl(); return; }
    try {
      await navigator.share({
        title: "Moovin Forward · FWA Expo · Aug 6, 2026",
        text: "Moovin Forward LLC · Institutional interior stabilization for DFW multifamily portfolios. Scan or tap to run a live triage on your asset:",
        url: fwaUrl,
      });
    } catch (e) { void e; }
  };

  return (
    <div data-testid="fwa-qr-root" style={{
      minHeight: "100vh", background: BG, color: WHITE, fontFamily: SANS,
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "0.9rem 1rem 1.6rem",
      backgroundImage: `radial-gradient(900px 500px at 50% 0%, ${CYAN}18 0%, transparent 60%), linear-gradient(180deg, ${BG} 0%, #030F2A 100%)`,
    }}>
      {/* Top bar */}
      <div style={{ width: "100%", maxWidth: 520, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <Link to="/fwa" style={{ color: SILVER, textDecoration: "none", fontFamily: MONO, fontSize: "0.64rem", letterSpacing: "0.16em" }} data-testid="fwa-qr-back">
          ← FWA_EXPO
        </Link>
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.64rem", letterSpacing: "0.22em" }}>
          BOOTH_CARD
        </div>
        <div style={{ width: 60 }} />
      </div>

      {/* Headline */}
      <div style={{ textAlign: "center", marginBottom: "0.9rem" }}>
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.66rem", letterSpacing: "0.28em", marginBottom: 4 }}>
          MOOVIN FORWARD LLC · DFW
        </div>
        <h1 style={{ fontSize: "clamp(1.5rem, 5vw, 2.2rem)", fontWeight: 800, margin: "0 0 0.3rem", lineHeight: 1.05, letterSpacing: "-0.02em" }} data-testid="fwa-qr-headline">
          Scan to enter.
        </h1>
        <div style={{ color: SILVER, fontSize: "0.82rem", lineHeight: 1.45, maxWidth: 380, margin: "0 auto" }}>
          FWA Expo · <span style={{ color: WHITE, fontWeight: 600 }}>Aug 6, 2026</span> · Hurst Conference Center
        </div>
      </div>

      {/* QR card */}
      <div data-testid="fwa-qr-card" style={{
        background: WHITE, borderRadius: 16, padding: "1rem 1rem 1.1rem",
        boxShadow: `0 30px 80px rgba(0,0,0,0.55), 0 0 40px ${CYAN}55`,
        maxWidth: 420, width: "100%",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "0.6rem",
      }}>
        <div style={{ color: BG, fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.2em", fontWeight: 700 }}>
          FWA_EXPO_2026 · TRIAGE_ENTRY
        </div>
        {fwaUrl ? (
          <div data-testid="fwa-qr-svg-wrap" style={{
            padding: 6, background: WHITE, borderRadius: 8,
            width: "min(74vw, 260px)", aspectRatio: "1 / 1",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <QRCodeSVG
              value={fwaUrl}
              size={260}
              bgColor="#FFFFFF"
              fgColor="#020A1F"
              level="H"
              includeMargin={false}
              style={{ width: "100%", height: "100%", display: "block" }}
            />
          </div>
        ) : (
          <div style={{ height: 260, width: 260, background: "#F1F3F8" }} />
        )}
        <div style={{ color: BG, fontFamily: MONO, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.02em", textAlign: "center", wordBreak: "break-all", lineHeight: 1.3 }} data-testid="fwa-qr-url">
          {fwaUrl.replace(/^https?:\/\//, "")}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, marginTop: "0.9rem", width: "100%", maxWidth: 420 }}>
        <button type="button" onClick={shareCard} data-testid="fwa-qr-share"
          style={{
            flex: 1, background: CYAN, color: BG, border: "none", borderRadius: 8,
            padding: "0.8rem 1rem", fontFamily: MONO, fontSize: "0.74rem",
            fontWeight: 800, letterSpacing: "0.08em", cursor: "pointer",
          }}>
          SHARE LINK
        </button>
        <button type="button" onClick={copyUrl} data-testid="fwa-qr-copy"
          style={{
            flex: 1, background: "transparent", color: CYAN, border: `1px solid ${CYAN}`,
            borderRadius: 8, padding: "0.8rem 1rem", fontFamily: MONO, fontSize: "0.74rem",
            fontWeight: 700, letterSpacing: "0.08em", cursor: "pointer",
          }}>
          COPY URL
        </button>
      </div>

      {/* How-to-use script */}
      <div style={{ maxWidth: 520, width: "100%", marginTop: "1.6rem",
                    background: "rgba(255,255,255,0.04)", border: `1px solid ${SILVER}33`,
                    borderRadius: 8, padding: "1rem 1.1rem" }} data-testid="fwa-qr-script">
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.22em", marginBottom: 6 }}>
          POCKET SCRIPT · HOLD PHONE UP
        </div>
        <div style={{ color: WHITE, fontSize: "0.9rem", lineHeight: 1.55, fontStyle: "italic" }}>
          &ldquo;Open your camera and point it at my screen — it&apos;ll drop a link.
          Tap it, tell me who you are, and I&apos;ll walk you into whichever side of the
          firm matches your seat: owner, capital, trade, or compliance.&rdquo;
        </div>
      </div>

      {/* How QR scanning actually works */}
      <div style={{ maxWidth: 520, width: "100%", marginTop: "0.9rem", padding: "0.9rem 1rem",
                    background: "rgba(255,255,255,0.03)", borderRadius: 6 }} data-testid="fwa-qr-howto">
        <div style={{ color: SILVER, fontFamily: MONO, fontSize: "0.6rem", letterSpacing: "0.18em", marginBottom: 6 }}>
          HOW THIS WORKS
        </div>
        <ol style={{ color: WHITE, fontSize: "0.82rem", lineHeight: 1.65, margin: 0, paddingLeft: "1.2rem" }}>
          <li>Attendee opens their phone camera (iPhone or Android).</li>
          <li>They point the camera at your screen — no touching required.</li>
          <li>A link notification pops up — they tap it.</li>
          <li>They land on <span style={{ color: CYAN, fontFamily: MONO }}>/fwa</span>, pick their role, and the site routes them.</li>
        </ol>
      </div>

      {/* Adaptive routing key */}
      <div style={{ maxWidth: 520, width: "100%", marginTop: "0.9rem", padding: "0.9rem 1rem",
                    background: "rgba(0,255,255,0.05)", border: `1px solid ${CYAN}44`, borderRadius: 6 }} data-testid="fwa-qr-routing">
        <div style={{ color: CYAN, fontFamily: MONO, fontSize: "0.6rem", letterSpacing: "0.18em", marginBottom: 6 }}>
          ADAPTIVE ROUTING · SAME QR, DIFFERENT DOORS
        </div>
        <ul style={{ color: WHITE, fontSize: "0.8rem", lineHeight: 1.65, margin: 0, paddingLeft: "1.1rem" }}>
          <li><span style={{ color: CYAN }}>Owner / PM / Investor / Broker</span> → live asset triage</li>
          <li><span style={{ color: CYAN }}>Vendor / Trade / 1099</span> → contractor application</li>
          <li><span style={{ color: CYAN }}>ADA / Section 504 Specialist</span> → compliance walkthrough</li>
          <li><span style={{ color: CYAN }}>Media / Sponsor / FWA Staff</span> → packet + capture</li>
        </ul>
      </div>

      <div style={{ marginTop: "1.4rem", color: SILVER, fontFamily: MONO, fontSize: "0.62rem", letterSpacing: "0.16em", textAlign: "center" }}>
        MOOVIN FORWARD LLC · INSTITUTIONAL INTERIOR STABILIZATION · DFW · BLUEPRINT_v2.5.0
      </div>
    </div>
  );
};

export default FWAQR;
